import { Component, useEffect } from "react";
import { BrowserRouter, Routes, Route, Navigate, useParams, useNavigate } from "react-router-dom";
import { Capacitor } from "@capacitor/core";
import { StatusBar, Style } from "@capacitor/status-bar";
import { App as CapApp } from "@capacitor/app";
import { AuthProvider } from "./context/AuthContext";
import { DriverAuthProvider } from "./context/DriverAuthContext";
import DriverApp from "./pages/DriverApp";
import LandingPage        from "./pages/LandingPage";
import DemoPage           from "./pages/DemoPage";
import AuthPage           from "./pages/AuthPage";
import ResetPasswordPage  from "./pages/ResetPasswordPage";
import OrgApp             from "./pages/OrgApp";
import AdminPage          from "./pages/AdminPage";
import GuestView          from "./pages/GuestView";
import TripGuestView      from "./pages/TripGuestView";
import TermsPage          from "./pages/TermsPage";
import PricingPage        from "./pages/PricingPage";
import ContactPage        from "./pages/ContactPage";
import PrivacyPage        from "./pages/PrivacyPage";
import RefundPage         from "./pages/RefundPage";
import PaymentCallback    from "./pages/PaymentCallback";
import PaymentSuccess     from "./pages/PaymentSuccess";
import PaymentFailed      from "./pages/PaymentFailed";
import { useIsMobile }   from "./hooks/useIsMobile";
import "./index.css";

class ErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, message: "" };
  }
  static getDerivedStateFromError(err) {
    return { hasError: true, message: err?.message || "Unexpected error" };
  }
  componentDidCatch(err, info) {
    console.error("[ErrorBoundary]", err, info?.componentStack);
  }
  render() {
    if (!this.state.hasError) return this.props.children;
    return (
      <div style={{
        minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center",
        background: "#0F172A", fontFamily: "system-ui, sans-serif", padding: 24,
      }}>
        <div style={{
          maxWidth: 420, width: "100%", background: "rgba(255,255,255,0.05)",
          border: "1px solid rgba(255,255,255,0.1)", borderRadius: 16, padding: "32px 28px",
          textAlign: "center",
        }}>
          <div style={{ fontSize: 40, marginBottom: 16 }}>⚠️</div>
          <h2 style={{ color: "#fff", fontSize: 20, fontWeight: 700, margin: "0 0 8px" }}>
            Something went wrong
          </h2>
          <p style={{ color: "rgba(255,255,255,0.5)", fontSize: 14, margin: "0 0 24px", lineHeight: 1.6 }}>
            {this.state.message || "An unexpected error occurred. Please refresh the page."}
          </p>
          <button
            onClick={() => window.location.reload()}
            style={{
              background: "#4F46E5", border: "none", color: "#fff",
              fontSize: 14, fontWeight: 600, padding: "10px 24px",
              borderRadius: 8, cursor: "pointer",
            }}
          >
            Reload Page
          </button>
        </div>
      </div>
    );
  }
}

// ── OWNER BYPASS — update REACT_APP_OWNER_CODE in .env to rotate the code ───
function OwnerPreview() {
  const { token } = useParams();
  const code = process.env.REACT_APP_OWNER_CODE;
  if (code && token === code) {
    localStorage.setItem("trackin_owner_bypass", code);
    return <Navigate to="/app" replace />;
  }
  return <Navigate to="/" replace />;
}
// ─────────────────────────────────────────────────────────────────────────────

// Pages that handle their own footer — global footer must not show on these
const NO_GLOBAL_FOOTER = ["/", "/app", "/pricing", "/contact", "/terms", "/privacy", "/refund"];

function Footer() {
  const isMobile = useIsMobile();
  if (isMobile) return null;
  if (typeof window !== "undefined" && NO_GLOBAL_FOOTER.includes(window.location.pathname)) return null;
  return (
    <div style={{
      position: "fixed", bottom: 0, left: 0, right: 0,
      padding: "5px 20px",
      fontSize: 11,
      color: "rgba(255,255,255,0.28)",
      letterSpacing: 0.3,
      zIndex: 9999,
      userSelect: "none",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
    }}>
      Made with ❤ in India by{" "}
      <a
        href="https://gvalabs.in/"
        target="_blank"
        rel="noopener noreferrer"
        style={{ marginLeft: 4, fontWeight: 700, color: "rgba(255,255,255,0.55)", textDecoration: "none", transition: "color 0.15s" }}
        onMouseEnter={e => e.currentTarget.style.color = "#818CF8"}
        onMouseLeave={e => e.currentTarget.style.color = "rgba(255,255,255,0.55)"}
      >
        GVALabs
      </a>
    </div>
  );
}

function CapacitorSetup() {
  const navigate = useNavigate();
  useEffect(() => {
    if (!Capacitor.isNativePlatform()) return;
    // Edge-to-edge: WebView overlays status bar, env(safe-area-inset-top) is
    // populated by the system with the real status bar height. Each page's
    // top container uses that env() to pad itself down.
    StatusBar.setOverlaysWebView({ overlay: true }).catch(() => {});
    StatusBar.setStyle({ style: Style.Dark }).catch(() => {}); // dark icons — visible on white Navbar
    // Android hardware back button — navigate back or exit app
    const handler = CapApp.addListener("backButton", () => {
      if (window.history.length > 1) {
        navigate(-1);
      } else {
        CapApp.exitApp();
      }
    });
    return () => { handler.then(h => h.remove()).catch(() => {}); };
  }, [navigate]);
  return null;
}

// Sets --sat / --sab CSS variables with a reliable mobile fallback.
// env(safe-area-inset-top) returns 0 in many Android/PWA contexts even when
// content IS behind the status bar — this detects that case and applies 28px.
function SafeAreaSetup() {
  useEffect(() => {
    function apply() {
      const probe = (prop) => {
        const el = document.createElement("div");
        el.style.cssText = `position:fixed;top:0;left:0;width:0;pointer-events:none;visibility:hidden;height:${prop}`;
        document.body.appendChild(el);
        const h = el.offsetHeight;
        document.body.removeChild(el);
        return h;
      };
      const satPx = probe("env(safe-area-inset-top,0px)");
      const sabPx = probe("env(safe-area-inset-bottom,0px)");
      // CSS already guarantees max(32px, env()) on mobile viewports.
      // Only override here if env() returns a real value larger than 32px
      // (e.g. tall notch on some devices) so CSS doesn't cap it.
      if (satPx > 32) document.documentElement.style.setProperty("--sat", satPx + "px");
      if (sabPx > 0)  document.documentElement.style.setProperty("--sab", sabPx + "px");
    }
    apply();
    window.addEventListener("resize", apply);
    return () => window.removeEventListener("resize", apply);
  }, []);
  return null;
}

export default function App() {
  return (
    <ErrorBoundary>
    <BrowserRouter>
      <AuthProvider>
      <DriverAuthProvider>
        <SafeAreaSetup />
        <CapacitorSetup />
        <Routes>
          <Route path="/"               element={<LandingPage />}       />
          <Route path="/demo"           element={<DemoPage />}          />
          <Route path="/auth"           element={<AuthPage />}          />
          <Route path="/signup/tracking" element={<AuthPage forcedTier="tracking" />} />
          <Route path="/signup/fleet"    element={<AuthPage forcedTier="fleet" />}    />
          <Route path="/reset-password" element={<ResetPasswordPage />} />
          <Route path="/app"            element={<OrgApp />}            />
          <Route path="/driver-app"     element={<DriverApp />}         />
          <Route path="/dhattgapochand"  element={<AdminPage />}         />
          <Route path="/owner/:token"   element={<OwnerPreview />}      />
          <Route path="/share/:token"   element={<GuestView />}         />
          <Route path="/trip/:token"    element={<TripGuestView />}     />
          <Route path="/terms"          element={<TermsPage />}         />
          <Route path="/pricing"        element={<PricingPage />}       />
          <Route path="/contact"        element={<ContactPage />}       />
          <Route path="/privacy"        element={<PrivacyPage />}       />
          <Route path="/refund"           element={<RefundPage />}         />
          <Route path="/payment/callback" element={<PaymentCallback />}    />
          <Route path="/payment/success"  element={<PaymentSuccess />}     />
          <Route path="/payment/failed"   element={<PaymentFailed />}      />
          <Route path="*"               element={<Navigate to="/" replace />} />
        </Routes>
        <Footer />
      </DriverAuthProvider>
      </AuthProvider>
    </BrowserRouter>
    </ErrorBoundary>
  );
}
